import { useState } from "react";
import { ChevronRight, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/use-language";
import tgaLogo from "@/assets/tga-logo.webp";
import tleLogo from "@/assets/tle-logo.webp";

interface SystemCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  shortDesc: string;
  longDesc: string;
  features: string[];
  standards: string[];
  delay: string;
  link: string;
  isPopular?: boolean;
  accentColor?: "gold" | "teal";
  showLabel: string;
  hideLabel: string;
  readMoreLabel: string;
}

const SystemCard = ({
  icon, title, subtitle, shortDesc, longDesc, features, standards, delay, link, isPopular, accentColor = "gold",
  showLabel, hideLabel, readMoreLabel
}: SystemCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const ringClass = accentColor === "teal" ? "ring-teal/50" : "ring-gold/50";
  const subtitleClass = accentColor === "teal" ? "text-teal-light" : "text-gold";
  const checkBgClass = accentColor === "teal" ? "bg-teal/20" : "bg-gold/20";
  const checkTextClass = accentColor === "teal" ? "text-teal-light" : "text-gold";
  
  return (
    <div 
      className={`relative bg-gradient-to-br from-navy-deep to-navy rounded-2xl p-8 lg:p-10 shadow-card hover:shadow-card-hover transition-all duration-500 border border-white/10 opacity-0 animate-slide-up group ${isPopular ? `ring-2 ${ringClass}` : ''}`} 
      style={{ animationDelay: delay }}
    >
      <div className="flex items-start gap-5 mb-6">
        <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl shadow-md group-hover:scale-105 transition-transform duration-300">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-display font-normal text-white mb-1">{title}</h3>
          <span className={`text-sm ${subtitleClass} font-semibold`}>{subtitle}</span>
        </div>
      </div>
      <p className="text-white/70 leading-relaxed mb-6">{shortDesc}</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {standards.map(standard => (
          <span key={standard} className="text-xs bg-white/10 text-white/80 font-medium px-3 py-1.5 rounded-lg border border-white/10">{standard}</span>
        ))}
      </div>
      <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="pt-6 border-t border-white/10">
          <p className="text-white/70 mb-6 leading-relaxed">{longDesc}</p>
          <ul className="space-y-3 mb-6">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3 text-sm text-white">
                <div className={`mt-0.5 w-5 h-5 rounded-full ${checkBgClass} flex items-center justify-center shrink-0`}>
                  <Check className={`w-3 h-3 ${checkTextClass}`} />
                </div>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex items-center justify-between pt-6 border-t border-white/10">
        <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors group/btn">
          <span>{isExpanded ? hideLabel : showLabel}</span>
          <ChevronRight className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? "rotate-90" : "group-hover/btn:translate-x-1"}`} />
        </button>
        <Link to={link}>
          <Button variant="hero" size="sm">
            {readMoreLabel}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

const Services = () => {
  const { t, localePath } = useLanguage();

  const systems = [{
    icon: <img loading="lazy" decoding="async" src={tgaLogo} alt={t("Tiego Group Accounting logotyp", "Tiego Group Accounting logo")} width={200} height={168} className="h-7 w-7 object-contain" />,
    title: "TGA",
    subtitle: "Tiego Group Accounting",
    shortDesc: t(
      "Komplett koncernredovisning som arbetar på transaktionsnivå. Automatisera elimineringar, valutaomräkningar och kassaflödesanalyser med full spårbarhet – byggt av redovisningsexperter.",
      "Complete group accounting that works at the transaction level. Automate eliminations, currency translations and cash flow analyses with full traceability – built by accounting experts."
    ),
    longDesc: t(
      "TGA hanterar hela processen från transaktionsnivå till färdig rapport. Systemet är byggt för komplexa koncernstrukturer med stöd för förvärv, fusioner, omstruktureringar och minoritetsintressen.",
      "TGA handles the entire process from the transaction level to finished reporting. The system is built for complex group structures with support for acquisitions, mergers, restructurings and minority interests."
    ),
    features: t(
      "Arbetar på transaktionsnivå för full spårbarhet och drill-down|Automatisk kassaflödesanalys enligt indirekt metod|Flexibla elimineringsmatriser för INA, förvärv och skatt|Hantering av förvärv, fusioner och avyttringar|Stöd för komplexa koncernstrukturer|Instruktionsfilmer och krönikor om redovisning",
      "Works at the transaction level for full traceability and drill-down|Automatic cash flow analysis using the indirect method|Flexible elimination matrices for INA, acquisitions and tax|Handling acquisitions, mergers and divestments|Support for complex group structures|Instructional videos and columns on accounting"
    ).split("|"),
    standards: ["K3", "IFRS"],
    link: localePath("/tga"),
    isPopular: true
  }, {
    icon: <img loading="lazy" decoding="async" src={tleLogo} alt={t("Tiego Lease Engine logotyp", "Tiego Lease Engine logo")} width={200} height={168} className="h-7 w-7 object-contain" />,
    title: "TLE",
    subtitle: "Tiego Lease Engine",
    shortDesc: t(
      "Automatiserad leasingredovisning för företag och offentlig sektor. Hantera IFRS 16, K3 och RKR R5 med ett molnbaserat system som förenklar komplexa beräkningar.",
      "Automated lease accounting for businesses and the public sector. Handle IFRS 16, K3 and RKR R5 with a cloud-based system that simplifies complex calculations."
    ),
    longDesc: t(
      "TLE automatiserar hela leasingredovisningen – från beräkning av nyttjanderättstillgångar och leasingskulder till hantering av indexeringar och avtalsförändringar. Perfekt för både privat och offentlig sektor.",
      "TLE automates the entire lease accounting process – from calculating right-of-use assets and lease liabilities to handling indexations and contract changes. Perfect for both private and public sector."
    ),
    features: t(
      "Automatiska beräkningar av tillgångar och skulder|Hantering av indexeringar och avtalsförändringar|Stöd för kommuner och regioner (RKR R5)|Molnbaserat – tillgängligt var som helst|Integration med era befintliga system|Komplett rapportering för bokslut",
      "Automatic calculations of assets and liabilities|Handling indexations and contract changes|Support for municipalities and regions (RKR R5)|Cloud-based – accessible anywhere|Integration with your existing systems|Complete reporting for closing"
    ).split("|"),
    standards: ["K3", "IFRS 16", "RKR R5"],
    link: localePath("/tle"),
    accentColor: "teal" as const
  }];

  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section id="systems" className="py-32 lg:py-44 bg-background relative">
      <div className="container mx-auto px-6 lg:px-8">
        <div ref={sectionRef} className={`max-w-3xl mx-auto text-center mb-20 lg:mb-28 transition-all duration-700 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block text-gold text-sm font-bold uppercase tracking-widest mb-4">
            {t("Våra system", "Our systems")}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-normal text-foreground mb-6">
            {t("Två kraftfulla lösningar för", "Two powerful solutions for")}{" "}
            <span className="italic text-navy">{t("era behov", "your needs")}</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t(
              "Specialbyggda system för koncernredovisning och leasinghantering – designade av redovisningsexperter för redovisningsexperter.",
              "Purpose-built systems for group accounting and lease accounting – designed by accounting experts for accounting experts."
            )}
          </p>
        </div>
        <div ref={cardsRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto transition-all duration-700 delay-200 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {systems.map((system, index) => (
            <SystemCard
              key={system.title}
              {...system}
              delay={`${index * 0.15}s`}
              showLabel={t("Visa funktioner", "Show features")}
              hideLabel={t("Visa mindre", "Show less")}
              readMoreLabel={t(`Läs mer om ${system.title}`, `Read more about ${system.title}`)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
